import CloakFetcher from 'cloak-auth/src/Client/CloakFetcher';
import { useGetInstances } from 'gen/client';
import { useEffect, useRef, useState } from 'react';

import { InstanceDescription } from 'kerberus-components/src/Types/AuthServer';

interface JoinedMetaItem {
    domain?: string;
    name?: string;
}

interface UseInstanceMetaResult {
    descriptions: Record<string, InstanceDescription>;
    ensureDescription: (id: string) => void;
    joinedMeta: Record<string, JoinedMetaItem>;
    joinedSet: Set<string>;
}

export default function useInstanceMeta(): UseInstanceMetaResult {
    const { data: joinedInstances = [] } = useGetInstances();
    const [descriptions, setDescriptions] = useState<Record<string, InstanceDescription>>({});
    const fetching = useRef<Set<string>>(new Set());
    const [joinedMeta, setJoinedMeta] = useState<Record<string, JoinedMetaItem>>({});
    const [joinedSet, setJoinedSet] = useState<Set<string>>(new Set());

    useEffect(() => {
        const meta: Record<string, JoinedMetaItem> = {};
        const set = new Set<string>();
        joinedInstances.forEach((inst) => {
            const idStr = String(inst.id);
            set.add(idStr);
            meta[idStr] = { domain: (inst as unknown as { domain?: string }).domain, name: inst.name };
        });
        setJoinedMeta(meta);
        setJoinedSet(set);
    }, [joinedInstances]);

    const ensureDescription = (id: string) => {
        if (descriptions[id] || fetching.current.has(id)) {
            return;
        }
        const domain = joinedMeta[id]?.domain;
        if (!domain) {
            return;
        }
        fetching.current.add(id);
        void CloakFetcher('tasks/instance_description', domain)
            .then((data: InstanceDescription | undefined) => {
                if (data) {
                    setDescriptions((prev) => (prev[id] ? prev : { ...prev, [id]: data }));
                }
            })
            .catch((error) => {
                // noop - ignore description fetch failures in hover context
                if (process.env.NODE_ENV === 'development') {
                    console.debug('description fetch failed', error);
                }
            })
            .finally(() => fetching.current.delete(id));
    };

    return { descriptions, ensureDescription, joinedMeta, joinedSet };
}
